import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress
} from "@mui/material"
import { Close, DeleteOutline, VerifiedOutlined } from "@mui/icons-material"
import { formatWhen } from "./datetime"
import ConfirmDialog from "./ConfirmDialog"

const MAX_BODY = 5000

/**
 * A question with its answers and a reply box. Used by both the student
 * player and the owner's inbox: the parent fetches the thread and performs
 * the mutations, this dialog only renders them and reports failures inline
 * (a toast would open underneath the dialog).
 *
 * `onReply`, `onDeleteQuestion` and `onDeleteAnswer` return promises that
 * reject with the RTK Query error.
 */
export default function QuestionThreadDialog({
    open,
    thread,
    isLoading,
    error,
    replyLabel = "Post answer",
    canDelete,
    onClose,
    onReply,
    onDeleteQuestion,
    onDeleteAnswer
}) {
    const [reply, setReply] = React.useState("")
    const [busy, setBusy] = React.useState(false)
    const [actionError, setActionError] = React.useState("")
    const [pendingDelete, setPendingDelete] = React.useState(null) // {kind, item}

    const question = _.get(thread, "question", null)
    const answers = _.get(thread, "answers", []) || []
    const questionId = question?.question_id

    /* A fresh thread starts with an empty reply box. */
    React.useEffect(() => {
        setReply("")
        setActionError("")
        setPendingDelete(null)
    }, [open, questionId])

    const run = async (action, fallback) => {
        setBusy(true)
        setActionError("")
        try {
            await action()
            return true
        } catch (failure) {
            setActionError(_.get(failure, "data.detail", null) || fallback)
            return false
        } finally {
            setBusy(false)
        }
    }

    const submitReply = async () => {
        const body = reply.trim()
        if (!body) return
        const ok = await run(() => onReply(body), "Could not post your reply.")
        if (ok) setReply("")
    }

    const confirmDelete = async () => {
        const { kind, item } = pendingDelete
        setPendingDelete(null)
        if (kind === "question") {
            await run(onDeleteQuestion, "Could not delete the question.")
        } else {
            await run(() => onDeleteAnswer(item), "Could not delete the answer.")
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            /* Full screen below sm so the thread is readable on a phone. */
            sx={{
                "& .MuiDialog-paper": {
                    m: { xs: 0, sm: 4 },
                    width: { xs: "100%", sm: "auto" },
                    maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
                    height: { xs: "100%", sm: "auto" },
                    borderRadius: { xs: 0, sm: 2 }
                }
            }}
        >
            <DialogTitle sx={{ pr: 6 }}>
                {question?.title || "Question"}
                {question && (
                    <span className="mt-0.5 block text-xs font-normal text-slate-500">
                        {question.author_name || "Student"}
                        {question.is_mine ? " (you)" : ""} ·{" "}
                        {formatWhen(question.created_at)}
                        {question.lecture_title
                            ? ` · ${question.lecture_title}`
                            : ""}
                    </span>
                )}
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading && (
                    <div className="flex justify-center py-10">
                        <CircularProgress size={28} />
                    </div>
                )}

                {!isLoading && error && <Alert severity="error">{error}</Alert>}

                {!isLoading && question && (
                    <div className="space-y-4">
                        <div className="rounded-xl bg-slate-50 p-3 sm:p-4">
                            <p className="whitespace-pre-line break-words text-sm text-slate-700">
                                {question.body}
                            </p>
                            {canDelete(question) && (
                                <div className="mt-2 flex justify-end">
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteOutline />}
                                        disabled={busy}
                                        onClick={() =>
                                            setPendingDelete({
                                                kind: "question",
                                                item: question
                                            })
                                        }
                                    >
                                        Delete question
                                    </Button>
                                </div>
                            )}
                        </div>

                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {answers.length
                                ? `${answers.length} ${
                                    answers.length === 1 ? "answer" : "answers"
                                }`
                                : "No answers yet"}
                        </h3>

                        <div className="space-y-3">
                            {answers.map((answer) => {
                                const instructor = answer.author_role === "owner"
                                return (
                                    <div
                                        key={answer.answer_id}
                                        className={`rounded-xl border p-3 ${
                                            instructor
                                                ? "border-emerald-200 bg-emerald-50/60"
                                                : "border-slate-200 bg-white"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                                                <span className="font-semibold text-slate-700">
                                                    {answer.author_name ||
                                                        (instructor
                                                            ? "Instructor"
                                                            : "Student")}
                                                </span>
                                                {instructor && (
                                                    <VerifiedOutlined
                                                        sx={{ fontSize: 14, color: "#059669" }}
                                                    />
                                                )}
                                                {answer.is_mine && <span>(you)</span>}
                                                <span>· {formatWhen(answer.created_at)}</span>
                                            </p>
                                            {canDelete(answer) && (
                                                <Tooltip title="Delete answer">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={busy}
                                                        onClick={() =>
                                                            setPendingDelete({
                                                                kind: "answer",
                                                                item: answer
                                                            })
                                                        }
                                                    >
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <p className="mt-1 whitespace-pre-line break-words text-sm text-slate-700">
                                            {answer.body}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>

                        {actionError && <Alert severity="error">{actionError}</Alert>}
                    </div>
                )}
            </DialogContent>

            {question && (
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 1
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        size="small"
                        placeholder="Write a reply..."
                        value={reply}
                        onChange={(event) =>
                            setReply(event.target.value.slice(0, MAX_BODY))
                        }
                        disabled={busy}
                    />
                    <div className="flex justify-end">
                        <Button
                            variant="contained"
                            onClick={submitReply}
                            disabled={busy || !reply.trim()}
                        >
                            {replyLabel}
                        </Button>
                    </div>
                </DialogActions>
            )}

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title={
                    pendingDelete?.kind === "question"
                        ? "Delete this question?"
                        : "Delete this answer?"
                }
                message={
                    pendingDelete?.kind === "question"
                        ? "The question and all of its answers will be removed."
                        : "This answer will be removed from the discussion."
                }
                busy={busy}
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </Dialog>
    )
}

QuestionThreadDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    thread: PropTypes.object,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    replyLabel: PropTypes.string,
    canDelete: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onReply: PropTypes.func.isRequired,
    onDeleteQuestion: PropTypes.func.isRequired,
    onDeleteAnswer: PropTypes.func.isRequired
}
